import { Icon } from '@synergy/icons';
import { Button, ConfigProvider, DatePicker, Modal, Tooltip } from "antd";
import enLocale from "antd/locale/en_GB";
import ptLocale from "antd/locale/pt_BR";
import dayjs from "dayjs";
import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import query from '../../../../../../../server/constants.api';
import useFetch from "../../../../../../../server/useFetch";
import Theme from "../../../../../../../styles/Theme/Theme";
import { NoData } from '../../../../../Atomics/NoData';
import { SelectAtomic } from "../../../../../Atomics/SelectAtomic";
import Spinner from '../../../../../Atomics/Spinner';
import { Widget } from "../../../../../Atomics/Widget";
import { getColorByChartMetricSubtype, getFormatDateByPeriodicity, periodicity } from '../DigitalTwinConfig/DigitalTwinConfig';

const FiltersContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: end;
    gap: 30px;
    margin-bottom: 40px;
    `;

const SelectContainer = styled.div`
    width: 160px;
`;

const ContentContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
`;

const LabelContainer = styled.label`
    font-weight: 500;
    display: flex;
    align-items: center;
    height: 35px;
    gap: 2px;
`;

export const DigitalTwinMetrics = ({ nodeId, isPopUpOpen, handleClose, ...props }) => {
    const { t } = useTranslation();
    const { RangePicker } = DatePicker;

    const periodicityOptionsData = [
        { label: t('daily'), value: periodicity.DAILY.periodicityType },
        { label: t('raw'), value: periodicity.RAW.periodicityType },
        { label: t('hourly'), value: periodicity.HOURLY.periodicityType },
        { label: t('monthly'), value: periodicity.MONTHLY.periodicityType },
        { label: t('yearly'), value: periodicity.YEARLY.periodicityType },
    ]

    // The default values are for the daily periodicity
    const [dates, setDates] = useState([dayjs().subtract(periodicity.DAILY.daysToDisable - 1, 'days'), dayjs()]);
    const [selectedPeriodicity, setSelectedPeriodicity] = useState(periodicity.DAILY.periodicityType);
    const [requestPeriodicity, setRequestPeriodicity] = useState(periodicity.DAILY.periodicityType);
    const [daysToDisable, setDaysToDisable] = useState(periodicity.DAILY.daysToDisable);
    const [pickerType, setPickerType] = useState('date');

    const language = i18next.resolvedLanguage;
    const currentLocale = language === 'pt' ? ptLocale : enLocale;
    const currentFormatDate = getFormatDateByPeriodicity(selectedPeriodicity, language);
    const currentFormatDateTime = currentFormatDate + ' HH:mm';

    /**
     * Disable all dates that are not in the range of 62 days
     * @param {Date} current The current date 
     * @returns 
     */
    const disabledDateDays = (current) => {
        if (!dates || (!dates[0] && !dates[1])) {
            return false;
        }

        const tooLate = dates[0] && current.diff(dates[0], 'days') > (daysToDisable - 1);
        const tooEarly = dates[1] && dates[1].diff(current, 'days') > (daysToDisable - 1);
        return !!tooEarly || !!tooLate;
    };

    /**
     * Format the date to the format yyyy-mm-ddT00:00:00
     * If the periodicity is not raw, the start time is 00:00:00 and the end time is 23:59:59 
     * When the periodicity is monthly, the end date is the last day of the month
     * When the periodicity is yearly, the start date is the first day of the year and the month is 1 and the end date is the last day of the year and the month is 12
     * @param {*} date 
     * @returns 
     */
    const formatDate = (date, isStartDate) => {
        if (!date) return null;
        const dateConverted = new Date(date);
        const datePart = dateConverted.toISOString().split('T')[0];

        let timePart;

        if (selectedPeriodicity === periodicity.RAW.periodicityType) {
            timePart = dateConverted.toLocaleTimeString([], { hour12: false });
        } else {
            timePart = isStartDate ? '00:00:00' : '23:59:59';
        }


        if (selectedPeriodicity === periodicity.MONTHLY.periodicityType ||
            selectedPeriodicity === periodicity.YEARLY.periodicityType) {
            const [year, month] = datePart.split('-');
            const isYearly = selectedPeriodicity === periodicity.YEARLY.periodicityType;
            const monthPart = isYearly ? (isStartDate ? '01' : '12') : month;
            const dayPart = isStartDate ? '01' : new Date(year, monthPart, 0).getDate();
            return `${year}-${monthPart}-${dayPart}T${timePart}`;
        }

        return `${datePart}T${timePart}`;
    }

    const { data: digitalTwinMetrics, refetch: refetchDigitalTwinMetrics, isFetching: isFetchingDigitalTwinMetrics, remove: removeDigitalTwinMetrics } = useFetch(
        "/metrics", "digitalTwinMetrics",
        {
            id: nodeId,
            periodicity: selectedPeriodicity,
            startDate: formatDate(dates?.[0], true),
            endDate: formatDate(dates?.[1])
        }, true, null, query.middlewareURL);

    /**
     * Check if the form is valid
     * @returns Boolean indicating if the form is valid
     */
    const isFormValid = () => {
        return dates?.[0] && dates?.[1] && selectedPeriodicity;
    }

    /**
     * Fetch the digital twin metrics when the form is submitted
     */
    const onSubmit = () => {
        setRequestPeriodicity(selectedPeriodicity);
        refetchDigitalTwinMetrics();
    }

    /**
     * Remove the digital twin metrics when the component is unmounted
     */
    useEffect(() => {
        return () => {
            removeDigitalTwinMetrics();
        };
    }, []);

    /**
     *  Check if there is data in the digital twin metrics
     * @returns {boolean} True if there is data, false otherwise 
     */
    const hasData = () => {
        return digitalTwinMetrics && Object.values(digitalTwinMetrics).some(field => Object.keys(field.data).length > 0);;
    }

    /**
     * Format the label based on the periodicity
     * @param {*} date Date to format
     * @returns Formatted label
     */
    const formatLabelByPeriodicity = (date) => {
        const [year, month, day, hour, minute] = date.split(/[-T:]/);

        switch (requestPeriodicity) {
            case periodicity.DAILY.periodicityType:
                return language === 'pt' ? `${day}/${month}` : `${month}/${day}`;
            case periodicity.RAW.periodicityType:
                return `${hour}:${minute}`;
            case periodicity.HOURLY.periodicityType:
                return `${hour}:${minute}`
            case periodicity.MONTHLY.periodicityType:
                return language === 'pt' ? `${month}/${year}` : `${year}/${month}`;
            case periodicity.YEARLY.periodicityType:
                return year;
            default:
                return i18next.resolvedLanguage === 'pt' ? `${day}/${month}` : `${month}/${day}`;
        }

    }

    /**
     * Handle the change of the periodicity
     * @param {*} value The new value for periodicity
     */
    const handlePeriodicityChange = (value) => {
        setDates(null);
        setSelectedPeriodicity(value)
        setDaysToDisable(periodicity[value]?.daysToDisable || periodicity[value]?.hoursToDisable);

        switch (value) {
            case periodicity.MONTHLY.periodicityType:
                setPickerType('month');
                break;
            case periodicity.YEARLY.periodicityType:
                setPickerType('year');
                break;
            default:
                setPickerType('date');
                break;
        }
    }

    /**
     * Get the data for the chart
     * @param {*} metricsData The metrics to show 
     * @param {*} metricSelected The name of metric selected
     * @param {*} metricsUnit The unit of the metric
     * @returns The data for the chart 
     */
    const metricsDataChart = (metricsData, metricSelected, metricsUnit) => {
        if (!metricsData || !metricSelected) return null;

        const seriesData = [];
        const labels = [];
        Object.entries(metricsData).map(([key, value]) => {
            const formattedLabel = formatLabelByPeriodicity(key);

            labels.push(formattedLabel);
            seriesData.push(value);
        });

        const chartColor = getColorByChartMetricSubtype(metricSelected);

        return {
            chartId: `mixedLineColumnChartApex-${metricsUnit}`,
            group: "metrics",
            series: [
                {
                    name: t(metricSelected),
                    type: "line", // line or scatter or column
                    data: seriesData,
                },
            ],
            categories: null,
            markerSize: 3,
            unit: metricsUnit,
            yAxisLabel: metricsUnit,
            columnWidth: 20,
            colors: [chartColor],
            labels: labels,
            markersSize: [6, 6],
            tickAmount: 10,
            yAxisLabelRotate: -90,
        }

    }

    /**
     * Memoized metrics data
     * Only update when the digitalTwinMetrics or language changes
     */
    const memoizedMetricsData = useMemo(() => {
        if (!digitalTwinMetrics) return [];
        const metricsData = Object.entries(digitalTwinMetrics)
            .filter(([key, data]) => Object.keys(data.data).length > 0)
            .map(([key, data]) => ({
                chartType: 'mixedLineColumnChartApex',
                chartId: `mixedLineColumnChartApex-${key}`,
                height: '350px',
                data: metricsDataChart(data.data, key, data.unit),
            }));

        return <Widget
            data={{
                background: "white",
                icon: null,
                height: '400px',
                charts: metricsData
            }}
        />
    }, [language, digitalTwinMetrics]);


    return (
        <Modal title={t('Metrics')} open={isPopUpOpen} onCancel={handleClose}
            footer={null} width="70rem"
            style={{ marginTop: '10px' }}
            styles={{
                body: {
                    overflowY: 'auto', maxHeight: 'calc(100vh - 210px)', overflowX: 'hidden',
                    padding: "0 50px"
                }
            }}
        >
            <FiltersContainer>
                <SelectContainer>
                    <LabelContainer>
                        {t("Periodicity")}
                        {selectedPeriodicity === periodicity.RAW.periodicityType &&

                            <Tooltip
                                title={t('Periodicity Tooltip')}
                                color={Theme.color.white}
                                colorText={Theme.color["coolgray-12"]}
                                className="widget-tooltip"
                            >
                                <Button
                                    shape="circle"
                                    type="text"
                                    icon={
                                        <Icon icon={"Information"} iconsize="xs" color="blue-4" />
                                    }
                                />
                            </Tooltip>}
                    </LabelContainer>

                    <SelectAtomic
                        optionsData={periodicityOptionsData}
                        fullWidth={true}
                        defaultValue={selectedPeriodicity}
                        handleOptionsChange={(value) => handlePeriodicityChange(value)}
                    />

                </SelectContainer>

                <ConfigProvider locale={currentLocale}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <LabelContainer>{t("Date")}</LabelContainer>
                        {selectedPeriodicity === periodicity.RAW.periodicityType ?
                            <DatePicker
                                onChange={(date) => date ? setDates([dayjs(date).subtract(periodicity.RAW.hoursToDisable, 'hours'), date]) : setDates(null)}
                                size="large"
                                showTime={{
                                    format: 'HH:mm',
                                }}
                                format={currentFormatDateTime}
                            />
                            :
                            <RangePicker
                                value={dates}
                                size="large"
                                disabledDate={disabledDateDays}
                                onCalendarChange={(val) => setDates(val)}
                                onOpenChange={(isOpen) => {
                                    if (!isOpen && (!dates?.[0] || !dates?.[1])) {
                                        setDates(null);
                                    }
                                }}
                                format={currentFormatDate}
                                picker={pickerType}
                            />
                        }
                    </div>
                </ConfigProvider>

                <Button
                    onClick={onSubmit}
                    style={{ height: '100%' }}
                    className="ant-btn ant-btn-blue"
                    disabled={!isFormValid() || isFetchingDigitalTwinMetrics}
                >
                    {t('apply')}
                </Button>

            </FiltersContainer>

            <div>

            </div>
            <ContentContainer>
                {(() => {
                    if (isFetchingDigitalTwinMetrics) {
                        return <Spinner stroke="green" />;
                    } else if (!hasData()) {
                        return <NoData />;
                    } else {
                        return memoizedMetricsData;
                    }
                })()}
            </ContentContainer>

        </Modal >
    )
};